import React from 'react';

interface ErrorBoundaryState {
    hasError: boolean;
    error?: Error;
}

interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // Filter out known third-party extension errors
        const isExtensionError = error.stack?.includes('extension://') ||
            error.stack?.includes('chrome-extension://') ||
            error.stack?.includes('moz-extension://') ||
            error.message?.includes('Extension context') ||
            error.message?.includes('message channel closed');

        const isReplayError = error.message?.includes('replayIntegration') ||
            error.message?.includes('replay');

        const isHMRError = error.message?.includes('HMR') ||
            error.message?.includes('hmr');

        // Only log non-extension, non-replay, non-HMR errors
        if (!isExtensionError && !isReplayError && !isHMRError) {
            console.error('ErrorBoundary caught an error:', error, errorInfo);
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: undefined });
    };

    render() {
        if (this.state.hasError && this.state.error) {
            if (this.props.fallback) {
                const FallbackComponent = this.props.fallback;
                return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
            }

            return (
                <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
                    <div className="text-center max-w-md">
                        <h2 className="text-2xl font-bold mb-4">Something went wrong</h2>
                        <p className="text-gray-400 mb-6">
                            An unexpected error occurred. Please try refreshing the page.
                        </p>
                        <button
                            onClick={this.resetError}
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
